/*
  cvc-recipe-render.js

  A JavaScript port of sections/main-recipe.liquid.

  WHY THIS EXISTS
  ---------------
  Two places need to draw a recipe without Shopify having rendered it:

    - /pages/recipes/<handle> for a recipe Tammy has only just added, which has
      no metaobject yet, so Shopify 404s it.
    - an already-rendered recipe page whose source has changed since the last
      sync, which is refreshed in place.

  Both have to be indistinguishable from the server's own output, so this is a
  literal port rather than a fresh design - same elements, same classes, same
  attribute order, and the same whitespace. scripts/render_parity_check.js
  proves it by diffing this against the real rendered pages, byte for byte.

  If you change main-recipe.liquid, change this and re-run that check. It runs
  inside scripts/sync.py, so the two cannot drift apart unnoticed.

  WHAT IT IS FED
  --------------
  The metaobject FIELD VALUES, not the raw source card - because that is what
  the Liquid reads. fieldValues() below is the port of import_all.field_values,
  so the full browser-side chain is:

      raw vendor card -> CVCTransform.transform -> fieldValues -> renderRecipe

  Two of the importer's field values cannot be reproduced in a browser and are
  passed in instead:
    - hero_image_url is re-hosted on Shopify's CDN by scripts/rehost_images.py.
      A recipe the sync has not seen yet has no CDN copy, so the live layer
      passes the vendor's own image proxy, which serves the same photograph.
    - similar[].handle is resolved server-side by card id against import state.
      The browser resolves it through assets/cvc-handles.json, which the
      exporter now publishes for exactly this purpose.
*/
(function (root, factory) {
  var dep = (typeof module === 'object' && module.exports)
    ? require('./cvc-recipe-transform.js')
    : root.CVCTransform;
  var api = factory(dep);
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.CVCRecipeRender = api;
})(typeof self !== 'undefined' ? self : this, function (T) {
  'use strict';

  // --- Liquid filters ------------------------------------------------------

  // Liquid's `escape`. Note &#39; for the apostrophe, which is what Shopify
  // emits - not &apos;.
  function esc(s) {
    return String(s === null || s === undefined ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // Liquid's `url_encode` is CGI escaping: a space becomes '+', not '%20'.
  function urlEncode(s) {
    return encodeURIComponent(String(s === null || s === undefined ? '' : s))
      .replace(/%20/g, '+')
      .replace(/[!'()*]/g, function (c) {
        return '%' + c.charCodeAt(0).toString(16).toUpperCase();
      });
  }

  // Liquid's `strip_html` just removes tags. It does NOT put a space where a
  // block element was, which is where it differs from transform.py's stripHtml.
  function stripHtmlLiquid(s) {
    return String(s === null || s === undefined ? '' : s)
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/<!--[\s\S]*?-->/g, '')
      .replace(/<style[\s\S]*?<\/style>/gi, '')
      .replace(/<[^>]*>/g, '');
  }

  // Liquid's `strip`.
  function strip(s) { return String(s === null || s === undefined ? '' : s).trim(); }

  // Liquid's `json`. Shopify's implementation also escapes the forward
  // slash, so a URL comes out as "https:\\/\\/..." - which matters here
  // because the output is compared byte for byte against the server's.
  function j(v) {
    return JSON.stringify(v === undefined ? null : v).replace(/\//g, '\\/');
  }

  // Liquid's `times: 1.0 | round`. Ruby rounds half away from zero; every
  // figure here is positive, where that agrees with Math.round.
  function roundNum(v) {
    var n = parseFloat(v);
    return isNaN(n) ? 0 : Math.round(n);
  }

  // Liquid's `truncate: 300` - 300 characters INCLUDING the '...' it appends.
  function truncate(s, n) {
    s = String(s);
    return s.length <= n ? s : s.slice(0, n - 3) + '...';
  }

  // snippets/cvc-duration.liquid: minutes -> "2 hrs 30 min".
  //
  // The trailing newline on the hours branch is not a mistake. In that snippet
  // the hours branch ends at `{%- else -%}` and the minutes branch at
  // `{%- endif -%}`, and Shopify strips the whitespace before `endif` but not
  // before `else` - so the server really does emit "2 hrs 15 min\n" and
  // "15 min". Reproduced here because the parity check compares against the
  // server's own output, and because that space is visible inside the chip.
  function duration(minutes) {
    var m = parseInt(minutes, 10) || 0;
    var h = Math.floor(m / 60);
    var rem = m % 60;
    if (h > 0) return h + ' ' + (h === 1 ? 'hr' : 'hrs') + (rem > 0 ? ' ' + rem + ' min' : '') + '\n';
    return m + ' min';
  }

  // Liquid treats "" as blank but NOT 0 or false.
  function blank(v) {
    return v === null || v === undefined || v === '' ||
           (Array.isArray(v) && v.length === 0);
  }

  // --- the port of import_all.field_values ---------------------------------

  // NEVER_BLANK in the importer: a blank hero url must never overwrite a good
  // one. Here it means the caller's image wins when the card has none.
  function fieldValues(r, opts) {
    opts = opts || {};
    var heroImage = opts.heroImage !== undefined ? opts.heroImage : r.image;
    // THE VIDEO GATE.
    //
    // Six of Tammy's recipe videos are private on YouTube and embedding them
    // renders a black "Video unavailable" player. scripts/check_videos.py
    // decides that by asking YouTube's oembed endpoint, which a storefront
    // script cannot do, so the verdict is shipped as data instead - the
    // handles asset carries it per card. Undefined means "no verdict on
    // record", and then the source card's own setting decides, which is what
    // the original site does.
    var videoOk = opts.videoOk;
    var handlesByCardId = opts.handlesByCardId || {};
    var namesByCardId = opts.namesByCardId || {};
    var imagesByCardId = opts.imagesByCardId || {};

    // similar_with_cdn: resolve by card id, drop what we cannot resolve, and
    // label the link with the target's CURRENT name rather than the stored one.
    var similar = [];
    for (var i = 0; i < r.similar.length; i++) {
      var s = r.similar[i];
      var handle = handlesByCardId[s.cardid];
      if (!handle) continue;
      similar.push({
        name: namesByCardId[s.cardid] || s.name || '',
        cardid: s.cardid,
        handle: handle,
        image: imagesByCardId[s.cardid] || s.image || ''
      });
    }

    return {
      title: r.display_title,
      summary: r.summary_html,
      notes: r.notes_html,
      hero_image_url: heroImage,
      yields: r.yields,
      scalable: !!r.scalable,
      prep_minutes: String(r.prep_minutes),
      cook_minutes: String(r.cook_minutes),
      total_minutes: String(r.total_minutes),
      categories: r.categories_raw,
      cuisine: (r.cuisine_raw && r.cuisine_raw[0]) || '',
      methods: r.methods_raw,
      cookbook_number: r.cookbook_number,
      ingredients: r.ingredients,
      instructions: r.instructions,
      nutrition: r.nutrition,
      affiliate_products: r.affiliate_products,
      youtube_url: (r.video_display && videoOk !== false) ? r.youtube_url : '',
      similar: similar,
      custom_time: r.custom_time,
      legacy_cardid: r.cardid
    };
  }

  // --- the port of the section's own liquid block --------------------------

  // Six ordered `contains` tests, then a three-way trim. Path forms are tested
  // before `v=` because a /live/ URL also contains 'youtube.com/'.
  function youtubeIdLiquid(yt) {
    yt = yt || '';
    var id = '';
    function after(sep) { var p = yt.split(sep); return p[p.length - 1]; }
    if (yt.indexOf('youtu.be/') !== -1) id = after('youtu.be/');
    else if (yt.indexOf('/embed/') !== -1) id = after('/embed/');
    else if (yt.indexOf('/shorts/') !== -1) id = after('/shorts/');
    else if (yt.indexOf('/live/') !== -1) id = after('/live/');
    else if (yt.indexOf('v=') !== -1) id = after('v=');
    else if (yt.indexOf('youtube.com/') !== -1) id = after('youtube.com/');
    return id.split('?')[0].split('&')[0].split('/')[0];
  }

  var NUTRITION_ROWS = [
    ['calories', 'Calories', ''],
    ['fat', 'Fat', ' g'],
    ['satfat', 'Sat. Fat', ' g'],
    ['carbs', 'Carbs', ' g'],
    ['fiber', 'Fiber', ' g'],
    ['netcarbs', 'Net carbs', ' g'],
    ['sugar', 'Sugar', ' g'],
    ['protein', 'Protein', ' g'],
    ['sodium', 'Sodium', ' mg'],
    ['cholesterol', 'Cholesterol', ' mg']
  ];

  // The JSON-LD omits netcarbs (schema.org has no equivalent) and tests
  // `!= blank` rather than truthiness, so a recorded 0 survives there.
  var LD_NUTRITION_ROWS = [
    ['calories', 'calories', ' calories'],
    ['fat', 'fatContent', ' g'],
    ['satfat', 'saturatedFatContent', ' g'],
    ['carbs', 'carbohydrateContent', ' g'],
    ['fiber', 'fiberContent', ' g'],
    ['sugar', 'sugarContent', ' g'],
    ['protein', 'proteinContent', ' g'],
    ['sodium', 'sodiumContent', ' mg'],
    ['cholesterol', 'cholesterolContent', ' mg']
  ];

  // Liquid truthiness: only nil and false are falsy, so "" and 0 are TRUE.
  // That is what the visible nutrition grid tests with `{%- if n.calories -%}`.
  function liquidTruthy(v) { return !(v === null || v === undefined || v === false); }

  // Two strings the recipe template now takes from section settings. They are
  // passed in rather than repeated here, or a live redraw would quietly put
  // the defaults back over whatever Tammy had changed them to. The fallbacks
  // match the schema defaults, so a caller that passes nothing still renders
  // the page the server renders - which is what keeps the parity check green.
  var DEFAULT_AUTHOR = 'Tammy Nichols';
  var DEFAULT_AFFILIATE_NOTE =
    'As an Amazon Associate, Collard Valley Cooks earns from qualifying purchases.';
  function authorFrom(ctx) { return (ctx && ctx.authorName) || DEFAULT_AUTHOR; }

  function renderRecipe(f, ctx) {
    ctx = ctx || {};
    var rootUrl = ctx.rootUrl || '/';
    // Two strings the recipe template now takes from section settings. They
    // are passed in rather than repeated here, or a live redraw would quietly
    // put the defaults back over whatever Tammy had changed them to. The
    // fallbacks match the schema defaults so an older caller still renders
    // the same page - which is what keeps render_parity_check.js green.
    var authorName = authorFrom(ctx);
    var affiliateNote = ctx.affiliateNote || DEFAULT_AFFILIATE_NOTE;
    // The cookbook link goes to the Cookbooks collection, not to every
    // product in the shop. It was the all-products URL, which dropped the
    // reader into the full catalogue when they clicked the book's name.
    var cookbookUrl = ctx.cookbookUrl || '/collections/cookbooks';
    var shareUrl = ctx.shareUrl || '';

    var yt = f.youtube_url || '';
    var ytId = youtubeIdLiquid(yt);
    var prep = parseInt(f.prep_minutes, 10) || 0;
    var cook = parseInt(f.cook_minutes, 10) || 0;
    var total = parseInt(f.total_minutes, 10) || 0;

    var out = '';
    out += '<div class="cvc-recipe" data-legacy-cardid="' + esc(f.legacy_cardid) + '">\n';
    out += '  <div class="cvc-wrapper">';

    if ((f.categories || []).length > 0) {
      out += '<div class="cvc-categories">';
      f.categories.forEach(function (c, i) {
        out += '<a href="' + rootUrl + 'pages/recipe-index?category=' + urlEncode(c) + '">' + c + '</a>';
        if (i !== f.categories.length - 1) out += ', ';
      });
      out += '</div>';
    }
    if (!blank(f.cuisine)) out += '<div class="cvc-cuisine">' + f.cuisine + '</div>';

    out += '<div class="cvc-info">';
    if (!blank(f.yields)) {
      out += '<div class="cvc-info-child"><strong>Yield: </strong><span' +
             (f.scalable ? ' data-cvc-yield="' + esc(f.yields) + '"' : '') +
             '>' + f.yields + '</span>';
      // The same dropdown Recipes Generator shows beside the yield.
      if (f.scalable) {
        out += '<select class="cvc-scale-select" data-cvc-scale aria-label="Adjust the recipe amounts" hidden>';
      out += '<option value="0.25">¼x</option>';
      out += '<option value="0.5">½x</option>';
      out += '<option value="1">1x</option>';
      out += '<option value="2">2x</option>';
      out += '<option value="3">3x</option>';
      out += '<option value="4">4x</option>';
      out += '<option value="5">5x</option>';
      out += '<option value="6">6x</option>';
      out += '<option value="7">7x</option>';
      out += '<option value="8">8x</option>';
      out += '<option value="9">9x</option>';
      out += '<option value="10">10x</option>';
        out += '</select>';
      }
      out += '</div>';
    }
    out += '<div class="cvc-info-child"><strong>Author: </strong><span>' + authorName + '</span></div>\n';
    out += '      <div class="cvc-btns-wrapper">\n';
    out += '        <button type="button" class="cvc-printbutton" onclick="window.print()">Print</button>\n';
    out += '      </div>\n';
    out += '    </div>\n\n';



    out += '    <div class="cvc-flex-b">';
    if (!blank(f.hero_image_url)) {
      out += '<div class="cvc-image">\n';
      out += '          <img src="' + f.hero_image_url + '" alt="' + esc(f.title) + '" loading="eager" width="600" height="600">\n';
      out += '        </div>';
    }
    out += '<div class="cvc-info-wrapper">\n';
    out += '        <h1 class="cvc-name">' + f.title + '</h1>';
    if (!blank(f.summary)) out += '<div class="cvc-summary">' + f.summary + '</div>';
    if (!blank(f.cookbook_number)) {
      out += '<p class="cvc-cookbook-note">\n';
      out += '            This is recipe <strong>#' + f.cookbook_number + '</strong> in\n';
      out += '            <a href="' + cookbookUrl + '">Tammy&rsquo;s Master Cookbook | Buy Now</a>.\n';
      out += '          </p>';
    }
    out += '</div>\n';
    out += '    </div>';

    var extra = f.custom_time || [];
    if (prep > 0 || cook > 0 || total > 0 || extra.length > 0) {
      out += '<div class="cvc-time">';
      if (prep > 0) out += '<span class="cvc-time-child"><span class="cvc-time-label">Prep time</span>: ' + duration(prep) + '</span>';
      if (cook > 0) out += '<span class="cvc-time-child"><span class="cvc-time-label">Cook time</span>: ' + duration(cook) + '</span>';
      if (total > 0) out += '<span class="cvc-time-child"><span class="cvc-time-label">Total time</span>: ' + duration(total) + '</span>';
      extra.forEach(function (c) {
        if (!(c.minutes > 0)) return;
        out += '<span class="cvc-time-child cvc-time-extra">\n';
        out += '              <span class="cvc-time-label">' + (blank(c.label) ? 'Also' : c.label) + '</span>: ' + duration(c.minutes) + '\n';
        out += '            </span>';
      });
      out += '</div>';
    }

    out += '<div class="cvc-cook-mode">\n';
    out += '      <button type="button" class="cvc-switch-toggle" aria-pressed="false" aria-label="Cook mode"></button>\n';
    out += '      <span class="cvc-cook-mode-label">Cook mode</span>\n';
    out += '      <span class="cvc-cook-mode-description">Prevent screen from turning off</span>\n';
    out += '    </div>';

    function groupBlock(groups, cls, listTag) {
      var s = '<div class="' + cls + '">\n';
      s += '        <h2 class="cvc-head">' + (cls === 'cvc-section-ingredients' ? 'Ingredients' : 'Instructions') + '</h2>';
      groups.forEach(function (g) {
        if (!blank(g.title)) s += '<h3 class="cvc-subhead">' + g.title + '</h3>';
        s += '<' + listTag + ' class="cvc-section-items">';
        (g.items || []).forEach(function (item) { s += '<li>' + item + '</li>'; });
        s += '</' + listTag + '>';
      });
      s += '</div>';
      return s;
    }

    if ((f.ingredients || []).length > 0) out += groupBlock(f.ingredients, 'cvc-section-ingredients', 'ul');
    if ((f.instructions || []).length > 0) out += groupBlock(f.instructions, 'cvc-section-instructions', 'ol');

    if (!blank(f.notes)) {
      out += '<div class="cvc-notes">\n';
      out += '        <h2 class="cvc-head">Notes</h2>\n';
      out += '        <div class="cvc-notes-inner">' + f.notes + '</div>\n';
      out += '      </div>';
    }

    if ((f.affiliate_products || []).length > 0) {
      out += '<div class="cvc-products">\n';
      out += '        <h2 class="cvc-head">Recommended Products:</h2>\n';
      out += '        <p class="cvc-affiliate-disclosure">\n';
      out += '          ' + affiliateNote + '\n';
      out += '        </p>\n';
      out += '        <div class="cvc-products-inner">';
      f.affiliate_products.forEach(function (p) {
        out += '<a class="cvc-product" href="' + p.link + '" target="_blank" rel="nofollow sponsored noopener">\n';
        out += '              <img class="cvc-product-image" src="' + p.image + '" alt="' + esc(p.name) + '" loading="lazy" width="150" height="150">\n';
        out += '              <span class="cvc-product-name">' + p.name + '</span>\n';
        out += '            </a>';
      });
      out += '</div>\n';
      out += '      </div>';
    }

    var n = f.nutrition || {};
    if (!blank(n) && Object.keys(n).length > 0) {
      out += '<div class="cvc-nutrition">\n';
      out += '        <h2 class="cvc-head">Nutrition Facts</h2>\n';
      out += '        <div class="cvc-nutrition-info">';
      NUTRITION_ROWS.forEach(function (row) {
        if (!liquidTruthy(n[row[0]])) return;
        out += '<div><p>' + row[1] + '</p>' + roundNum(n[row[0]]) + row[2] + '</div>';
      });
      out += '</div>\n';
      out += '      </div>';
    }

    if ((f.similar || []).length > 0) {
      out += '<div class="cvc-similar-posts">\n';
      out += '        <h2 class="cvc-head">Similar Recipes</h2>\n';
      out += '        <div class="cvc-similar-posts-wrapper">';
      f.similar.forEach(function (s) {
        out += '<a class="cvc-similar-post cvc-tile" href="' + rootUrl + 'pages/recipes/' + s.handle + '">';
        if (!blank(s.image)) {
          out += '<img class="cvc-similar-post-image" src="' + s.image + '" alt="' + esc(s.name) + '"\n';
          out += '                     loading="lazy" width="200" height="143">';
        }
        out += '<span class="cvc-similar-post-name">' + s.name + '</span>\n';
        out += '            </a>';
      });
      out += '</div>\n';
      out += '      </div>';
    }

    if (!blank(ytId)) {
      out += '<div class="cvc-video">';
      if (!blank(f.title)) out += '<span class="cvc-video-title">' + f.title + '</span>';
      out += '<span class="cvc-video-url">' + f.youtube_url + '</span>';
      out += '</div>\n';
      out += '      <div class="cvc-video-wrapper">\n';
      out += '        <iframe class="cvc-video-embed" src="https://www.youtube.com/embed/' + ytId + '"\n';
      out += '                title="' + esc(f.title) + '" loading="lazy" allowfullscreen></iframe>\n';
      out += '      </div>';
    }

    out += '<div class="cvc-share-buttons">';
    out += '<a href="https://www.facebook.com/sharer/sharer.php?u=' + shareUrl + '" target="_blank" rel="noopener" aria-label="Share on Facebook">Facebook</a>\n';
    out += '      <a href="https://pinterest.com/pin/create/button/?url=' + shareUrl + '&description=' + urlEncode(f.title) + '&media=' + urlEncode(f.hero_image_url) + '" target="_blank" rel="noopener" aria-label="Share on Pinterest">Pinterest</a>\n';
    out += '      <a href="mailto:?subject=' + urlEncode(f.title) + '&body=' + shareUrl + '" aria-label="Share by email">Email</a>\n';
    out += '    </div>\n\n';
    out += '  </div>\n';
    out += '</div>';
    return out;
  }

  // --- the schema.org Recipe block ----------------------------------------

  function entities(s) {
    return String(s).split('&nbsp;').join(' ').split('&amp;').join('&');
  }

  // Cut at a sentence, not a character count, up to 300 - with the hard
  // truncate only as a fallback when the first sentence already exceeds it.
  function ldDescription(summaryHtml) {
    var rd = strip(entities(stripHtmlLiquid(summaryHtml)));
    if (rd.length <= 300) return rd;
    var parts = rd.split('. ');
    var out = '';
    for (var i = 0; i < parts.length; i++) {
      var candidate = out + parts[i] + '.';
      if (candidate.length > 300) break;
      out = candidate + ' ';
    }
    out = strip(out);
    if (out === '' || out === '.') out = truncate(rd, 300);
    return out;
  }

  function renderRecipeLd(f, ctx) {
    var ytId = youtubeIdLiquid(f.youtube_url || '');
    var prep = parseInt(f.prep_minutes, 10) || 0;
    var cook = parseInt(f.cook_minutes, 10) || 0;
    var total = parseInt(f.total_minutes, 10) || 0;
    var n = f.nutrition || {};

    // 256 ingredient lines across the library end in a colon and not one is a
    // real ingredient, so the colon marks a section header. Instructions get
    // the same entity treatment but NOT the colon test - two real steps end
    // in one.
    var ings = [];
    (f.ingredients || []).forEach(function (g) {
      (g.items || []).forEach(function (item) {
        var ing = strip(entities(stripHtmlLiquid(item)));
        if (ing !== '' && ing.slice(-1) !== ':') ings.push(ing);
      });
    });
    var steps = [];
    (f.instructions || []).forEach(function (g) {
      (g.items || []).forEach(function (item) {
        var s = strip(entities(stripHtmlLiquid(item)));
        if (s !== '') steps.push(s);
      });
    });
    var cats = (f.categories || []).map(function (c) { return strip(stripHtmlLiquid(c)); }).filter(Boolean);
    var meths = (f.methods || []).map(function (m) { return strip(stripHtmlLiquid(m)); }).filter(Boolean);

    var s = '';
    s += '{\n';
    s += '  "@context": "https://schema.org",\n';
    s += '  "@type": "Recipe",\n';
    s += '  "name": ' + j(strip(stripHtmlLiquid(f.title))) + ',\n';
    s += '  "author": { "@type": "Person", "name": ' + j(authorFrom(ctx)) + ' }';
    if (!blank(f.hero_image_url)) s += ',"image": [' + j(f.hero_image_url) + ']';
    if (!blank(f.summary)) s += ',"description": ' + j(ldDescription(f.summary));
    if (!blank(f.yields)) s += ',"recipeYield": ' + j(strip(stripHtmlLiquid(f.yields)));
    if (prep > 0) s += ',"prepTime": "PT' + prep + 'M"';
    if (cook > 0) s += ',"cookTime": "PT' + cook + 'M"';
    if (total > 0) s += ',"totalTime": "PT' + total + 'M"';
    if (cats.length) s += ',"recipeCategory": [' + cats.map(j).join(',') + ']';
    if (!blank(f.cuisine)) s += ',"recipeCuisine": ' + j(strip(stripHtmlLiquid(f.cuisine)));
    if (meths.length) s += ',"cookingMethod": [' + meths.map(j).join(',') + ']';

    var ldNut = '';
    LD_NUTRITION_ROWS.forEach(function (row) {
      if (blank(n[row[0]])) return;
      ldNut += ',"' + row[1] + '": "' + roundNum(n[row[0]]) + row[2] + '"';
    });
    if (ldNut !== '') s += ',"nutrition": { "@type": "NutritionInformation"' + ldNut + ' }';

    if (!blank(ytId)) {
      s += ',"video": {\n';
      s += '      "@type": "VideoObject",\n';
      s += '      "name": ' + j(strip(stripHtmlLiquid(f.title))) + ',\n';
      s += '      "embedUrl": ' + j('https://www.youtube.com/embed/' + ytId) + ',\n';
      s += '      "thumbnailUrl": ' + j('https://img.youtube.com/vi/' + ytId + '/hqdefault.jpg') + '\n';
      s += '    }';
    }
    if (ings.length) s += ',"recipeIngredient": [' + ings.map(j).join(',') + ']';
    if (steps.length) s += ',"recipeInstructions": [' + steps.map(function (t) {
      return '{ "@type": "HowToStep", "text": ' + j(t) + ' }';
    }).join(',') + ']';
    s += '}';
    return s;
  }

  return {
    fieldValues: fieldValues,
    renderRecipe: renderRecipe,
    renderRecipeLd: renderRecipeLd,
    duration: duration,
    youtubeIdLiquid: youtubeIdLiquid,
    esc: esc,
    urlEncode: urlEncode,
    ldDescription: ldDescription
  };
});
